import { AxiosError, AxiosResponse } from 'axios';

import {
	FeedApi,
	FeedTank,
	FeedTankLegShapes,
	FeedTankLegShapesEnum,
	InlineResponse20017,
	InlineResponse20018,
	Org,
	OrgsApi,
	OrgsInData,
	Product,
	ProductsApi,
	User,
	UsersInData,
} from '@/api';
import { FeedProduct, feedProductIncludes, FeedType } from '../models';
import { ApiUtils } from '@/common/ApiUtils';

export class FeedProductService {
	constructor(
		private productApi: ProductsApi = new ProductsApi(),
		private feedApi: FeedApi = new FeedApi(),
		private orgApi: OrgsApi = new OrgsApi(),
	) {}

	public async getFeedTypes(): Promise<{ feedTypes?: FeedType[]; errorMessage?: string }> {
		try {
			const feedTypesResponse = await this.feedApi.getFeedTypes();
			return { feedTypes: feedTypesResponse.data.data as FeedType[] };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to fetch feed types');
		}
	}

	public async fetchProduct(
		feedProductId: number,
	): Promise<{ product?: FeedProduct; errorMessage?: string }> {
		try {
			const productResponse: AxiosResponse<Product> = await this.productApi.getProductById(
				feedProductId,
				{ params: { include: feedProductIncludes } },
			);
			return { product: productResponse.data as FeedProduct };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem fetching the feed product');
		}
	}

	public async fetchAllowedUserIds(
		feedProductId: number,
	): Promise<{ allowedUserIds?: number[] | null; errorMessage?: string }> {
		try {
			const productResponse: AxiosResponse<InlineResponse20017> = await this.productApi.getProductAllowedUsers(
				feedProductId,
			);
			return { allowedUserIds: productResponse.data.data || null };
		} catch (e) {
			return ApiUtils.handleApiError(
				e,
				"There was a problem fetching the feed product's allowed user ids",
			);
		}
	}

	public async updateProduct(
		updatedFeedProduct: FeedProduct,
	): Promise<{ errorMessage?: string } | void> {
		if (!updatedFeedProduct.id) {
			return { errorMessage: 'This feed product has no id' };
		}
		try {
			await this.productApi.updateProduct(updatedFeedProduct.id, {
				data: { ...updatedFeedProduct },
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to update feed product');
		}
	}

	public async getFeedTankLegShapes(): Promise<{ legShapes?: FeedTankLegShapes[]; errorMessage?: string }> {
		try {
			const response = await this.feedApi.getFeedTankLegShapes();
			return { legShapes: response.data.data ?? [] };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to fetch leg shapes');
		}
	}

	public async createFeedTank(params: {
		productId: number;
		nominalVolume?: number;
		nominalWeight?: number;
		name: string;
		sensorType: string;
		numLegs?: number | null;
		legShape?: FeedTankLegShapesEnum | null;
		brand?: string | null;
	}): Promise<{ tank?: FeedTank; errorMessage?: string }> {
		try {
			const createFeedTankResponse: AxiosResponse<FeedTank> = await this.feedApi.feedTankAdd({
				data: params,
			});
			return { tank: createFeedTankResponse.data };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem creating a new feed tank');
		}
	}

	public async fetchPartnerOrgs(
		productId: number,
	): Promise<{ orgs?: Org[]; errorMessage?: string }> {
		try {
			const partnerOrgsResponse: AxiosResponse<OrgsInData> = await this.productApi.getProductPartnerOrgs(
				productId,
			);
			return { orgs: partnerOrgsResponse.data.data ?? [] };
		} catch (e) {
			return ApiUtils.handleApiError(e, "There was a problem fetching the product's partner orgs");
		}
	}

	public async fetchOrgUsers(orgId: number): Promise<{ users?: User[]; errorMessage?: string }> {
		try {
			const orgUsersResponse: AxiosResponse<UsersInData> = await this.orgApi.getOrgUsers(orgId);
			return { users: orgUsersResponse.data.data ?? [] };
		} catch (e) {
			return ApiUtils.handleApiError(e, "There was a problem fetching the organisation's users");
		}
	}
}
